import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { TaskItem } from '@demind-inc/core';

import { tasksApi } from '../api';
import { TasksByDate } from '../types';
import { getIsOverdue } from '../../helpers';
import { TEAM_TASKS_QUERY_KEY } from '../queries/teamTasks.shared';

interface UseUpdateTodoTaskParams {
  taskId: string;
  completed: boolean;
  boardId: string;
  userId: string;
  mutateParams?: {
    date?: string;
    parentOriginalTaskId?: string;
  };
}
export const useUpdateTaskStatus = () => {
  const queryClient = useQueryClient();

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, completed, boardId, userId }: UseUpdateTodoTaskParams) => {
      return tasksApi
        .updateCompleteStatusOfTask(taskId, boardId, userId, completed)
        .then(({ data }) => data);
    },
    onMutate: async ({ taskId, completed, mutateParams }) => {
      const isOverdue = mutateParams?.date ? getIsOverdue(mutateParams.date) : false;
      const shouldSyncOverdueTasks = isOverdue || !mutateParams?.date;

      if (shouldSyncOverdueTasks) {
        await queryClient.cancelQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
        queryClient.setQueriesData<TaskItem[]>(
          { queryKey: ['lifestack.todo.overdueTasks'] },
          (prevTasks) => {
            if (mutateParams?.parentOriginalTaskId) {
              // subtask
              return prevTasks?.map((task) =>
                task.originalTaskId === mutateParams?.parentOriginalTaskId
                  ? {
                      ...task,
                      subTasks: task.subTasks?.map((subtask) =>
                        subtask.taskId === taskId ? { ...subtask, completed } : subtask
                      ),
                    }
                  : task
              );
            }
            return prevTasks?.map((task) =>
              task.taskId === taskId ? { ...task, completed } : task
            );
          }
        );
      }

      if (!isOverdue) {
        const queryKey = mutateParams?.date
          ? ['lifestack.todo.tasks', { dueDate: mutateParams.date }]
          : ['lifestack.todo.tasks'];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueriesData<TasksByDate>({ queryKey }, (prevTasks) => {
          if (mutateParams?.parentOriginalTaskId) {
            // subtask
            return {
              date: prevTasks?.date,
              tasks: prevTasks?.tasks?.map((task) => {
                if (task.originalTaskId === mutateParams?.parentOriginalTaskId) {
                  return {
                    ...task,
                    subTasks: task.subTasks?.map((subtask) =>
                      subtask.taskId === taskId ? { ...subtask, completed } : subtask
                    ),
                  };
                }
                return task;
              }),
              updatedAt: new Date().toISOString(),
            };
          }
          return {
            date: prevTasks?.date,
            tasks: prevTasks?.tasks?.map((task) =>
              task.taskId === taskId ? { ...task, completed } : task
            ),
            updatedAt: new Date().toISOString(),
          };
        });
      }
    },
    onSuccess: (response, { mutateParams }) => {
      const isOverdue = mutateParams?.date ? getIsOverdue(mutateParams.date) : false;
      const shouldSyncOverdueTasks = isOverdue || !mutateParams?.date;

      queryClient.invalidateQueries({ queryKey: TEAM_TASKS_QUERY_KEY, type: 'active' });

      if (shouldSyncOverdueTasks) {
        queryClient.invalidateQueries({ queryKey: ['lifestack.todo.overdueTasks'] });
      }

      if (!isOverdue) {
        queryClient.invalidateQueries({
          queryKey: mutateParams?.date
            ? ['lifestack.todo.tasks', { dueDate: mutateParams.date }]
            : ['lifestack.todo.tasks'],
        });
      }

      if (response?.nextDueDate) {
        queryClient.invalidateQueries({
          queryKey: ['lifestack.todo.tasks', { dueDate: response.nextDueDate }],
        });
      }
    },
  });

  return {
    updateTodoTaskStatus: updateTaskMutation.mutateAsync,
    isTaskStatusUpdating: updateTaskMutation.isPending,
    ...updateTaskMutation,
  };
};
